import { Spin } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';

import { workflowsApi } from '@/api/workflows';
import { isDevLoginEnabled } from '@/auth/devLogin';
import { normalizeLanguage } from '@/i18n';

/**
 * Landing point for an advisor arriving with `applicationType` and/or
 * `applicationId` on the URL (bmo-workflow-platform-spec.md:35, forwarded
 * through ISAM login by AuthEndpoints.BuildContextRedirectUrl). Only one of
 * the two is required — either is enough to avoid the "nothing to go on"
 * bail below — but today only `applicationType` can actually be RESOLVED:
 * it's the workflow's `appName`, resolved via `workflowsApi.getByAppName()`
 * to get a template id, and the advisor bounces straight into a FormShell
 * runtime, the /advisor/dashboard picker never mounting on this path.
 * `applicationId` (a specific advisor-client application instance) takes
 * precedence when both are present — resume that exact application under
 * the template `applicationType` resolved. When `applicationId` is present
 * WITHOUT `applicationType`, there is no lookup that goes applicationId →
 * templateId (see the TODO below), so this still falls back exactly like
 * the "nothing at all" case for now — that's the known gap, not a bug.
 * Falls back to the picker only in dev-login (QA/demo) sessions —
 * /advisor/dashboard is itself gated to dev-login via RequireDevLogin (spec
 * §11, "Advisors — no dashboard"), so a real advisor session lands on the
 * advisor-entry error page instead.
 *
 * TODO(BMO-ADVISOR-INTEGRATION): applicationId-only entry (resume with no
 * applicationType on the URL) has no resolution path today and is a known
 * pain point for the real "retrieve application" work — see
 * docs/pretend-advisor.md swap-point #9 ("resume") for the write-up:
 * `applicationsApi.get` needs `templateId` as an input, which is exactly
 * what we'd be trying to discover from `applicationId` alone here.
 */
export function AdvisorEntryPage() {
  const [searchParams] = useSearchParams();
  const { i18n } = useTranslation();
  const applicationType = searchParams.get('applicationType');
  const applicationId = searchParams.get('applicationId');
  const mode = searchParams.get('mode');
  const language = searchParams.get('language');

  // undefined = still resolving; null = fall back to the picker; string = navigate there.
  const [target, setTarget] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    // Normalised, not exact-matched: BMO controls this value and their own
    // pages live under `/fr-ca/`, so `fr-CA` is a likelier arrival than `fr`.
    // An exact match dropped it and served English — silently.
    const resolved = normalizeLanguage(language);
    if (resolved) void i18n.changeLanguage(resolved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!applicationType && !applicationId) {
      setTarget(null);
      return;
    }
    if (!applicationType) {
      // applicationId alone can't be resolved to a workflow today — see the
      // applicationId-only TODO above. Same outcome as "nothing at all" for
      // now, reached via a distinct branch so the gap is visible rather than
      // silently folded into the "no params" case.
      setTarget(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const match = await workflowsApi.getByAppName(applicationType);
        if (cancelled) return;
        // isActive + currentVersionNumber > 0, not status === 'Approved': status tracks
        // whichever version is currently in-flight, not whether a version is live. A
        // workflow with a new version being drafted or re-approved sits at
        // status === 'Draft'/'Pending' while its previous version keeps serving advisors,
        // and must still resolve here — same "is this actually live" predicate as
        // ListPublishedAsync (DynamoDbWorkflowRepository) and the public workflows endpoint.
        if (!match || !match.isActive || match.currentVersionNumber <= 0) {
          setTarget(null);
          return;
        }
        // applicationId takes precedence when present (resume that exact
        // application); when absent, still redirect — into a fresh
        // application on this workflow via the existing mint route rather
        // than falling back to the picker.
        const dest = applicationId
          ? `/advisor/workflows/${match.id}/applications/${applicationId}`
          : `/advisor/workflows/${match.id}/applications/new`;
        setTarget(mode ? `${dest}?mode=${encodeURIComponent(mode)}` : dest);
      } catch {
        if (!cancelled) setTarget(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applicationType, applicationId, mode]);

  if (target === undefined) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Navigate
      to={
        target ??
        (isDevLoginEnabled() ? '/advisor/dashboard' : '/auth/error?cause=advisor-entry-required')
      }
      replace
    />
  );
}
